import { useEffect, useMemo, useRef, useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Trophy as TrophyIcon } from 'lucide-react'
import { tierStyle } from './trophyShared'
import type { OriginalTrophy } from '@/api/trophies'
import { OWNED_LIST_DROP_ID, parseDragId, toDragId } from '@/lib/shelf-layout'

// ドラッグ終了直後に届くクリックを無視する猶予（タッチ長押し→離した時の誤タップ対策）
const CLICK_GUARD_MS = 300

/** ロゴ部分だけをドラッグの掴み手にする。タップ（ドラッグなし）は詳細を開く */
function DragHandle({ trophy, onTap }: { trophy: OriginalTrophy; onTap: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: toDragId({ from: 'list', trophyId: trophy.id }),
  })
  const wasDragging = useRef(false)
  const dragEndedAt = useRef(0)
  useEffect(() => {
    if (wasDragging.current && !isDragging) dragEndedAt.current = Date.now()
    wasDragging.current = isDragging
  }, [isDragging])

  const { color, glow } = tierStyle(trophy.rarity)
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      aria-label={`${trophy.name}をドラッグして棚に飾る`}
      onClick={() => { if (Date.now() - dragEndedAt.current > CLICK_GUARD_MS) onTap() }}
      className={`flex h-11 w-11 shrink-0 touch-none items-center justify-center ${isDragging ? 'opacity-30' : ''}`}
    >
      <TrophyIcon size={24} style={{ color, filter: `drop-shadow(0 0 4px ${glow})` }} />
    </button>
  )
}

/** 棚の下に表示する、所有トロフィーの一覧（新しい順）。棚から落とすと棚から外れる。 */
export default function OwnedTrophyList({
  trophies,
  onTap,
}: {
  trophies: OriginalTrophy[]
  onTap: (t: OriginalTrophy) => void
}) {
  const [excludePlaced, setExcludePlaced] = useState(true)
  const { setNodeRef, isOver, active } = useDroppable({ id: OWNED_LIST_DROP_ID })
  // 棚から持ってきた時だけ落とし先として見せる
  const acceptsDrop = active !== null && parseDragId(String(active.id))?.from === 'shelf'

  const list = useMemo(() => {
    const filtered = excludePlaced ? trophies.filter((t) => t.position === null) : trophies
    return [...filtered].sort((a, b) => b.created_at.localeCompare(a.created_at))
  }, [trophies, excludePlaced])

  return (
    <div
      ref={setNodeRef}
      className={`relative min-h-[120px] space-y-2 rounded-lg ${
        acceptsDrop ? (isOver ? 'ring-2 ring-primary' : 'ring-2 ring-primary/30') : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">所有トロフィー</p>
        <label className="flex select-none items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={excludePlaced}
            onChange={(e) => setExcludePlaced(e.target.checked)}
            className="h-3.5 w-3.5 accent-primary"
          />
          飾っているものを除く
        </label>
      </div>

      {list.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">
          {excludePlaced ? '未配置のトロフィーはありません' : 'トロフィーがありません'}
        </p>
      ) : (
        <div className="space-y-1.5">
          {list.map((t) => (
            <div key={t.id} className="flex w-full items-center gap-1 rounded-lg border pl-1 pr-3 hover:bg-muted/50">
              <DragHandle trophy={t} onTap={() => onTap(t)} />
              <button
                type="button"
                onClick={() => onTap(t)}
                className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 py-2 text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{t.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {t.title && `${t.title}・`}レア度 {t.rarity}
                    <span className="ml-1">・ {new Date(t.created_at).toLocaleDateString('ja-JP')}</span>
                  </p>
                </div>
                {t.position !== null && (
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                    飾り中
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* レイアウトを動かさないよう重ねて表示（ドラッグ中に落とし先の位置がずれるのを防ぐ） */}
      {acceptsDrop && (
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center rounded-lg bg-background/70 pt-10">
          <p className="rounded-full bg-primary px-3 py-1 text-xs text-primary-foreground shadow">
            ここに落とすと棚から外します
          </p>
        </div>
      )}
    </div>
  )
}
